import { CreateRecord, Operation, op } from "libs/transaction";
import { GroupTagRecord, SpecialTagTypeEnum, generateRecordId } from "libs/schema";
import { ADMIN_USER_ID } from "libs/shared-constants";
import { omitBy } from "lodash-comms";

export type CreateGroupProps = {
  groupId?: string;
  icon: string | null;
  name: string;
  description?: string | null;
  isPrivateGroup: boolean;
  creatorUserId: string;
  /**
   * Array of group IDs that this group should be nested under in the
   * sidebar.
   */
  nestedInGroupIds: string[];
  /**
   * An array of organization IDs. If a user is added to one of these
   * organizations, they should be automatically subscribed to this group.
   */
  subscribeNewUsersOfOrgs?: string[];
  ownerOrganizationId: string;
};

export function createGroup(props: CreateGroupProps) {
  const groupId = props.groupId ?? generateRecordId("tag");
  const organization_group_member_ids = props.isPrivateGroup ? [] : [props.ownerOrganizationId];

  const operations: Operation[] = [];

  const group = op.set("tag", {
    id: groupId,
    type: SpecialTagTypeEnum.GROUP,
    name: props.name,
    icon: props.icon,
    description: props.description ?? null,
    // The presence of a property with an undefined value will cause the
    // database to throw an error.
    data: omitBy(
      {
        organization_group_member_ids,
        subscribe_new_users_of_orgs: props.subscribeNewUsersOfOrgs,
      },
      (value) => value === undefined,
    ),
    owner_organization_id: props.ownerOrganizationId,
    archived_at: null,
  } satisfies CreateRecord<GroupTagRecord>);

  operations.push(group);

  const groupFolders = props.nestedInGroupIds.map((folderId) => {
    return op.set("tag_folder_member", {
      id: generateRecordId("tag_folder_member", {
        tag_id: groupId,
        folder_id: folderId,
      }),
      creator_user_id: props.creatorUserId,
      folder_id: folderId,
      tag_id: groupId,
      tag_type: SpecialTagTypeEnum.GROUP,
      owner_organization_id: props.ownerOrganizationId,
    });
  });

  operations.push(...groupFolders);

  if (!props.isPrivateGroup) {
    const groupMember = op.set("tag_group_member", {
      id: generateRecordId("tag_group_member", {
        tag_id: groupId,
        group_id: props.ownerOrganizationId,
      }),
      creator_user_id: props.creatorUserId,
      group_id: props.ownerOrganizationId,
      tag_id: groupId,
      tag_type: SpecialTagTypeEnum.GROUP,
      is_organization_group: true,
      owner_organization_id: props.ownerOrganizationId,
    });

    operations.push(groupMember);
  } else if (props.creatorUserId !== ADMIN_USER_ID) {
    // If the group is private, than we add the current user as the first member
    // of the group.
    const userMember = op.set("tag_user_member", {
      id: generateRecordId("tag_user_member", {
        tag_id: groupId,
        user_id: props.creatorUserId,
      }),
      tag_id: groupId,
      tag_type: SpecialTagTypeEnum.GROUP,
      user_id: props.creatorUserId,
      creator_user_id: props.creatorUserId,
      owner_organization_id: props.ownerOrganizationId,
    });

    operations.push(userMember);
  }

  return operations;
}

/* -----------------------------------------------------------------------------------------------*/

export type CreateGeneralGroupProps = {
  groupId?: string;
  creatorUserId: string;
  ownerOrganizationId: string;
};

export function createGeneralGroup(props: CreateGeneralGroupProps) {
  return createGroup({
    groupId: props.groupId,
    name: "General",
    icon: "⭐️",
    description: null,
    creatorUserId: props.creatorUserId,
    isPrivateGroup: false,
    nestedInGroupIds: [props.ownerOrganizationId],
    ownerOrganizationId: props.ownerOrganizationId,
    subscribeNewUsersOfOrgs: [props.ownerOrganizationId],
  });
}

/* -----------------------------------------------------------------------------------------------*/
